import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FaApple, FaAndroid, FaShieldAlt, FaSync, FaBolt, FaGlobe, FaUserFriends, FaLock } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';

const PageContainer = styled.div`
  padding-top: 2rem;
  background: linear-gradient(135deg, rgba(0, 213, 146, 0.03) 0%, rgba(0, 213, 146, 0) 100%);
  position: relative;
  overflow-x: hidden;
  
  &::before {
    content: '';
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d592' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    z-index: -1;
    pointer-events: none;
    opacity: 0.8;
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.alternate ? 'rgba(240, 247, 255, 0.7)' : props.theme.colors.white};
  position: relative;
  overflow: hidden;
  
  ${props => props.alternate && `
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: 
        radial-gradient(circle at 15% 85%, rgba(0, 213, 146, 0.08) 0%, transparent 40%),
        radial-gradient(circle at 85% 15%, rgba(0, 213, 146, 0.08) 0%, transparent 40%);
      z-index: 0;
    }
  `}
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f0fffa 0%, #e6f7f2 100%);
  padding: 7rem 0 8rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(0, 213, 146, 0.1) 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, rgba(0, 213, 146, 0.1) 0%, transparent 40%),
      url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2300d592' fill-opacity='0.03' fill-rule='evenodd'%3E%3Cpath d='M0 0h20L0 20z'/%3E%3C/g%3E%3C/svg%3E");
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(90deg, 
      transparent 0%,
      ${props => props.theme.colors.primary} 50%,
      transparent 100%
    );
    opacity: 0.3;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.8rem;
  margin-bottom: 1.8rem;
  color: ${props => props.theme.colors.secondary};
  position: relative;
  z-index: 1;
  font-weight: 800;
  line-height: 1.2;
  
  span {
    color: ${props => props.theme.colors.primary};
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 8px;
      background: rgba(0, 213, 146, 0.2);
      bottom: 8px;
      left: 0;
      z-index: -1;
      border-radius: 10px;
    }
  }
  
  &::before {
    content: '{...}';
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(0, 213, 146, 0.2);
    font-size: 1.5rem;
    font-weight: normal;
    opacity: 0.8;
    font-family: monospace;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 3rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  color: ${props => props.theme.colors.gray};
  line-height: 1.7;
  position: relative;
  z-index: 1;
  
  &::after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, 
      transparent 0%,
      ${props => props.theme.colors.primary} 50%,
      transparent 100%
    );
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 1.2rem;
  }
`;

const WalletGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 4.5rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
  
  > div {
    width: 380px;
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    top: -80px;
    left: 10%;
    z-index: -1;
    opacity: 0.7;
  }
`;

const WalletCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  text-align: center;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 213, 146, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.05) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 0;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    right: -100px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0.5;
    transition: all 0.4s ease;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-15px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 213, 146, 0.3);
    color:#fff !important;
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: translate(-30px, -30px);
    }
  }
`;

const WalletIcon = styled.div`
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a080);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  color: white;
  font-size: 2.8rem;
  position: relative;
  box-shadow: 0 10px 20px rgba(0, 213, 146, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a080);
    border-radius: 26px;
    opacity: 0.3;
    z-index: -1;
    filter: blur(8px);
  }
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 110%;
    height: 110%;
    border: 1px dashed rgba(255, 255, 255, 0.4);
    border-radius: 26px;
    animation: wallet-spin 40s linear infinite;
  }
  
  @keyframes wallet-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const WalletTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1.2rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
    border-radius: 3px;
  }
`;

const WalletDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: 1.05rem;
  line-height: 1.7;
  margin: 1.5rem 0 2rem;
  position: relative;
  z-index: 1;
`;

// Button group removed

const FeaturesSection = styled.section`
  padding: 7rem 0;
  background: linear-gradient(135deg, #f0fffa 0%, #e6f7f2 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at 15% 15%, rgba(0, 213, 146, 0.08) 0%, transparent 40%),
      radial-gradient(circle at 85% 85%, rgba(0, 213, 146, 0.08) 0%, transparent 40%);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2300d592' fill-opacity='0.03' fill-rule='evenodd'%3E%3Cpath d='M15 0C6.716 0 0 6.716 0 15c0 8.284 6.716 15 15 15 8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15zm0 6c4.971 0 9 4.029 9 9s-4.029 9-9 9-9-4.029-9-9 4.029-9 9-9z'/%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
    z-index: 0;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 4rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 18px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 213, 146, 0.1);
  height: 100%;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 50px;
    background: linear-gradient(to bottom, ${props => props.theme.colors.primary}, transparent);
    border-top-left-radius: 18px;
    opacity: 0.7;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    right: -50px;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0.5;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 213, 146, 0.3);
    
    &::after {
      transform: translate(-20px, -20px);
    }
  }
  
  h3 {
    font-size: 1.35rem;
    margin-bottom: 1.2rem;
    color: ${props => props.theme.colors.secondary};
    font-weight: 600;
    position: relative;
    padding-left: 2.2rem;
    
    svg {
      position: absolute;
      left: 0;
      top: 0.2rem;
      color: ${props => props.theme.colors.primary};
      font-size: 1.5rem;
    }
  }
  
  p {
    color: ${props => props.theme.colors.gray};
    font-size: 1rem;
    line-height: 1.7;
  }
`;

const SetupSection = styled.section`
  padding: 7rem 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.05) 0%, transparent 70%);
    border-radius: 50%;
    top: 10%;
    right: 5%;
    z-index: 0;
  }
`;

const SetupSteps = styled.div`
  margin-top: 4rem;
  position: relative;
  padding-left: 3rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 30px;
    top: 0;
    bottom: 60px;
    width: 3px;
    background: linear-gradient(to bottom, 
      ${props => props.theme.colors.primary},
      rgba(0, 213, 146, 0.2)
    );
    border-radius: 3px;
    z-index: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding-left: 0;
    
    &::before {
      left: 30px;
    }
  }
`;

const Step = styled.div`
  display: flex;
  margin-bottom: 4rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    padding-left: 60px;
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a080);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin-right: 2.5rem;
  box-shadow: 0 8px 20px rgba(0, 213, 146, 0.2);
  position: relative;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    width: 70px;
    height: 70px;
    border: 1px dashed rgba(0, 213, 146, 0.3);
    border-radius: 50%;
    animation: step-spin 40s linear infinite;
  }
  
  @keyframes step-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    position: absolute;
    left: 0;
    top: 0;
    margin-bottom: 1.5rem;
    margin-right: 0;
  }
`;

const StepContent = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 213, 146, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 213, 146, 0.3);
  }
  
  h3 {
    font-size: 1.4rem;
    margin-bottom: 1.2rem;
    color: ${props => props.theme.colors.secondary};
    font-weight: 600;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -6px;
      left: 0;
      width: 40px;
      height: 3px;
      background: ${props => props.theme.colors.primary};
      border-radius: 3px;
    }
  }
  
  p {
    color: ${props => props.theme.colors.gray};
    font-size: 1.05rem;
    line-height: 1.7;
    margin-bottom: 1.2rem;
  }
  
  code {
    background-color: #f8f9fa;
    padding: 0.5rem 0.8rem;
    border-radius: 8px;
    font-family: 'Fira Code', monospace;
    font-size: 0.95rem;
    color: #333;
    border: 1px solid rgba(0, 213, 146, 0.1);
    display: inline-block;
    margin-top: 0.3rem;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
  }
`;

const CTASection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #f0fffa 0%, #e6f7f2 100%);
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at 30% 40%, rgba(0, 213, 146, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 70% 60%, rgba(0, 213, 146, 0.08) 0%, transparent 50%);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(90deg, 
      ${props => props.theme.colors.primary} 0%, 
      transparent 100%
    );
    opacity: 0.3;
  }
`;

const CTATitle = styled.h2`
  font-size: 2.8rem;
  margin-bottom: 1.8rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 700;
  position: relative;
  z-index: 2;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, 
      transparent 0%,
      ${props => props.theme.colors.primary} 50%,
      transparent 100%
    );
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 2.2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
  color: ${props => props.theme.colors.gray};
  line-height: 1.8;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 1.1rem;
  }
`;

const Wallet = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Wallets | RDT Chain - Apps, Extensions & Setup Instructions</title>
      </Helmet>
      <HeroSection>
        <Container>
          <HeroTitle>
            RDT Chain <span>Wallet</span>
          </HeroTitle>
          <HeroDescription>
            Access the RDT Chain ecosystem with our official wallets. Securely store, send, and receive RDT tokens with zero transaction fees.
          </HeroDescription>
        </Container>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle
            subtitle="Download"
            title="Official RDT Chain Wallet"
            description="Choose the wallet that fits your device and needs. All wallets support the core RDT Chain features and provide secure access to your assets."
            center
          />

          <WalletGrid>
            <WalletCard>
              <WalletIcon>
                <FaApple />
              </WalletIcon>
              <WalletTitle>iOS Wallet</WalletTitle>
              <WalletDescription>
                Our iOS wallet application provides secure access to RDT Chain with biometric authentication and intuitive interface.
              </WalletDescription>
              <Button href="https://apps.apple.com/app/rdtchain" target="_blank" rel="noopener noreferrer">
                Download for iOS
              </Button>
            </WalletCard>

            <WalletCard>
              <WalletIcon>
                <FaAndroid />
              </WalletIcon>
              <WalletTitle>Android Wallet</WalletTitle>
              <WalletDescription>
                The Android wallet app offers fast, secure access to RDT Chain with hardware wallet support and real-time notifications.
              </WalletDescription>
              <Button href="rdt.apk" target="_blank" rel="noopener noreferrer">
                Download for Android
              </Button>
              {/* <Button href="https://play.google.com/store/apps/details?id=com.rdtchain.wallet" target="_blank" rel="noopener noreferrer">
                Download for Android
              </Button> */}
            </WalletCard>

          </WalletGrid>
        </Container>
      </Section>

      <FeaturesSection>
        <Container>
          <SectionTitle
            subtitle="Capabilities"
            title="RDT Wallet Features"
            description="Our wallets are designed to provide the best user experience while ensuring maximum security for your digital assets."
            center
          />

          <FeatureGrid>
            <FeatureCard>
              <h3>Security First</h3>
              <p>All wallets employ industry-leading security standards including biometric authentication, hardware wallet support, and encrypted backups to keep your assets safe.</p>
            </FeatureCard>

            <FeatureCard>
              <h3>Zero Fee Transactions</h3>
              <p>Send and receive RDT tokens with absolutely no transaction fees, making microtransactions and frequent transfers practical and economical.</p>
            </FeatureCard>

            <FeatureCard>
              <h3>Instant Confirmations</h3>
              <p>With RDT Chain's 5-second block time, transactions are confirmed rapidly, providing near-instant finality for your transfers.</p>
            </FeatureCard>

            <FeatureCard>
              <h3>DApp Browser</h3>
              <p>Browse and interact with decentralized applications built on RDT Chain directly from your wallet with our integrated DApp browser.</p>
            </FeatureCard>

            <FeatureCard>
              <h3>Cross-Chain Support</h3>
              <p>Manage assets across multiple blockchain networks from a single interface with built-in bridge functionality for seamless asset transfers.</p>
            </FeatureCard>

            <FeatureCard>
              <h3>Multi-Account Management</h3>
              <p>Create and manage multiple accounts with customizable labels for enhanced organization of your assets and activities.</p>
            </FeatureCard>
          </FeatureGrid>
        </Container>
      </FeaturesSection>

      <SetupSection>
        <Container>
          <SectionTitle
            subtitle="Getting Started"
            title="Setting Up MetaMask for RDT Chain"
            description="Follow these simple steps to configure MetaMask wallet to work with RDT Chain network."
            center
          />

          <SetupSteps>
            <Step>
              <StepNumber>1</StepNumber>
              <StepContent>
                <h3>Install MetaMask Extension</h3>
                <p>If you don't already have MetaMask installed, visit the <a href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer">official MetaMask website</a> and add the extension to your browser.</p>
              </StepContent>
            </Step>

            <Step>
              <StepNumber>2</StepNumber>
              <StepContent>
                <h3>Open MetaMask Settings</h3>
                <p>Click on the MetaMask extension icon in your browser, then click on your account icon in the top right, and select "Settings" from the dropdown menu.</p>
              </StepContent>
            </Step>

            <Step>
              <StepNumber>3</StepNumber>
              <StepContent>
                <h3>Navigate to Networks</h3>
                <p>In the Settings menu, click on "Networks" to access the network configuration options. Then click the "Add Network" button.</p>
              </StepContent>
            </Step>

            <Step>
              <StepNumber>4</StepNumber>
              <StepContent>
                <h3>Add RDT Chain Network</h3>
                <p>Enter the RDT Chain network parameters:</p>
                <p>
                  <strong>Network Name:</strong> RDT Chain<br />
                  <strong>RPC URL:</strong> <code>https://rpc-supernova.rdtchain.com</code><br />
                  <strong>Chain ID:</strong> <code>875423</code><br />
                  <strong>Currency Symbol:</strong> RDT<br />
                  <strong>Block Explorer URL:</strong> <code>https://explorer.rdtchain.com</code>
                </p>
              </StepContent>
            </Step>

            <Step>
              <StepNumber>5</StepNumber>
              <StepContent>
                <h3>Select RDT Chain Network</h3>
                <p>After adding the network, select "RDT Chain" from the network dropdown menu at the top of the MetaMask interface. Now you're connected to RDT Chain and can start transacting with zero fees!</p>
              </StepContent>
            </Step>
          </SetupSteps>
        </Container>
      </SetupSection>

      <CTASection>
        <Container>
          <CTATitle>Ready to Experience Zero-Fee Transactions?</CTATitle>
          <CTADescription>
            Download the RDT Chain wallet today and join the revolution in cross-border payments with zero transaction fees.
          </CTADescription>
          <Button href="#wallet-downloads" secondary>
            Get RDT Wallet
          </Button>
        </Container>
      </CTASection>
    </PageContainer>
  );
};

export default Wallet;